import type { RealtimePostgresChangesPayload } from '@supabase/supabase-js'
import { supabase } from './supabase'

/**
 * Subscribe to row-level Postgres changes scoped to one organization on one table.
 * Returns an unsubscribe function suitable for use in `useEffect` cleanup.
 *
 * @example
 *   useEffect(() => subscribeOrgChannel(orgId, 'projects', (p) => {
 *     queryClient.invalidateQueries({ queryKey: ['projects'] })
 *   }), [orgId])
 */
export function subscribeOrgChannel<T extends Record<string, unknown> = Record<string, unknown>>(
  orgId: string,
  table: string,
  handler: (payload: RealtimePostgresChangesPayload<T>) => void,
): () => void {
  const ch = supabase
    .channel(`org:${orgId}:${table}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table, filter: `organization_id=eq.${orgId}` },
      (payload) => handler(payload as RealtimePostgresChangesPayload<T>),
    )
    .subscribe()

  return () => {
    void supabase.removeChannel(ch)
  }
}
